import { Toaster, toast, type Toast } from 'react-hot-toast';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';

// ─── Tipos ───────────────────────────────────────────────────────────────────

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

interface ToastPayload {
  title: string;
  description?: string;
  duration?: number;
}

// ─── Estilos por variante (alineados con el design system del dashboard) ──────

const variantStyles: Record<
  ToastVariant,
  { icon: React.ReactNode; bar: string; iconColor: string }
> = {
  success: {
    icon: <CheckCircle size={18} strokeWidth={2} />,
    bar: '#22c55e',
    iconColor: '#22c55e',
  },
  error: {
    icon: <XCircle size={18} strokeWidth={2} />,
    bar: '#800F2F',
    iconColor: '#800F2F',
  },
  warning: {
    icon: <AlertTriangle size={18} strokeWidth={2} />,
    bar: '#f59e0b',
    iconColor: '#f59e0b',
  },
  info: {
    icon: <Info size={18} strokeWidth={2} />,
    bar: '#3b82f6',
    iconColor: '#3b82f6',
  },
};

// ─── Renderizador del toast personalizado ────────────────────────────────────

function renderToast(
  t: Toast,
  variant: ToastVariant,
  title: string,
  description?: string
) {
  const { icon, bar, iconColor } = variantStyles[variant];

  return (
    <div
      className={`
        flex items-start gap-3 w-full max-w-sm
        bg-white rounded-xl shadow-lg border border-page-border
        overflow-hidden pr-3 py-3 pl-0
        transition-all duration-300
        ${t.visible ? 'animate-fade-scale' : 'opacity-0 scale-95'}
      `}
      style={{ minWidth: 280 }}
    >
      {/* Barra lateral de color */}
      <div
        className="self-stretch w-1 rounded-full flex-shrink-0 ml-3"
        style={{ backgroundColor: bar }}
      />

      {/* Ícono */}
      <span className="mt-0.5 flex-shrink-0" style={{ color: iconColor }}>
        {icon}
      </span>

      {/* Contenido */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-ink leading-snug">{title}</p>
        {description && (
          <p className="text-xs text-ink-muted mt-0.5 leading-relaxed">
            {description}
          </p>
        )}
      </div>

      {/* Botón cerrar */}
      <button
        onClick={() => toast.dismiss(t.id)}
        className="flex-shrink-0 mt-0.5 text-ink-faint hover:text-ink transition-colors duration-150 rounded-md p-0.5"
        aria-label="Cerrar notificación"
      >
        <X size={14} strokeWidth={2.5} />
      </button>
    </div>
  );
}

// ─── Hook público ─────────────────────────────────────────────────────────────

/**
 * `useToast` — hook para disparar toasts informativos desde cualquier componente.
 *
 * @example
 * const { success, error, warning, info } = useToast();
 * success({ title: 'Guardado', description: 'Los cambios se aplicaron.' });
 */
export function useToast() {
  const show = (variant: ToastVariant, payload: ToastPayload) => {
    const { title, description, duration = 4000 } = payload;

    toast.custom(
      (t) => renderToast(t, variant, title, description),
      { duration, position: 'top-center' }
    );
  };

  return {
    success: (payload: ToastPayload) => show('success', payload),
    error:   (payload: ToastPayload) => show('error',   payload),
    warning: (payload: ToastPayload) => show('warning', payload),
    info:    (payload: ToastPayload) => show('info',    payload),
  };
}

// ─── Provider (colocar una sola vez en el layout raíz) ───────────────────────

/**
 * `ToastProvider` — monta el contenedor de `react-hot-toast`.
 * Colocarlo una vez en `App.tsx` o en el layout principal.
 *
 * @example
 * <ToastProvider />
 */
export function ToastProvider() {
  return (
    <Toaster
      position="top-center"
      gutter={10}
      containerStyle={{ top: 24 }}
      toastOptions={{
        // Desactiva los toasts nativos; sólo usamos `toast.custom`
        duration: 4000,
        style: { padding: 0, background: 'transparent', boxShadow: 'none' },
      }}
    />
  );
}
